import React from "react";
import { Box, Typography, Badge, useMediaQuery, useTheme } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useUser } from "../../context/UserContext";

const BottomNavMobile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const { totalItems } = useCart();
  const { user } = useUser();
  const rawRoles = user?.roles || user?.role || [];
  const rolesList = Array.isArray(rawRoles)
    ? rawRoles
    : typeof rawRoles === "string"
      ? rawRoles.split(",").map(r => r.trim())
      : [];
  const isStaff = rolesList.some(r => ["admin", "super admin"].includes(String(r).trim().toLowerCase()));

  if (!isMobile) return null;

  const handleNavClick = (item) => {
    if (item.label === "Account" || item.label === "Sign In") {
      navigate(user ? "/account" : "/login");
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const navItems = [
    {
      label: "Home",
      icon: <HomeIcon fontSize="medium" />,
      path: "/",
    },
    {
      label: "Shop",
      icon: <StorefrontIcon fontSize="medium" />,
      path: "/shop",
    },
    {
      label: "Deals",
      icon: <LocalOfferIcon fontSize="medium" />,
      path: "/shop?category=combo",
    },
    {
      label: "Cart",
      icon: (
        <Badge badgeContent={totalItems} color="primary" sx={{ "& .MuiBadge-badge": { fontSize: "0.7rem", bgcolor: '#0E6ADA', color: '#FFF' } }}>
          <ShoppingCartIcon fontSize="medium" />
        </Badge>
      ),
      path: "/cart",
    },
    {
      label: user ? "Account" : "Sign In",
      icon: <PersonOutlineIcon fontSize="medium" />,
      path: user ? "/account" : "/login",
    },
  ];

  const isActive = (itemPath, label) => {
    if (!itemPath) return false;
    if (label === "Account" || label === "Dashboard" || label === "Sign In") {
      return ["/account", "/dashboard", "/login", "/register", "/profile"].some(p => location.pathname.startsWith(p));
    }
    const [pathPart, queryPart] = itemPath.split("?");
    if (pathPart === "/") return location.pathname === "/" && !location.search;
    if (queryPart) {
      return location.pathname === pathPart && location.search.includes(queryPart);
    }
    if (pathPart === "/shop" && location.search.includes("category=combo")) {
      return false;
    }
    return location.pathname.startsWith(pathPart);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        bgcolor: "#fff",
        borderTop: "1px solid #e0e0e0",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        height: 60,
        boxShadow: "0px -2px 10px rgba(0,0,0,0.08)",
        pb: 'env(safe-area-inset-bottom)',
      }}
    >
      {navItems.map((item, idx) => {
        const active = isActive(item.path, item.label);
        return (
          <Box
            key={idx}
            onClick={() => handleNavClick(item)}
            sx={{
              textAlign: "center",
              minWidth: 56,
              cursor: "pointer",
              py: 0.5,
              color: active ? '#0E6ADA' : '#666',
              transition: 'color 0.2s',
              "&:hover": { color: '#0E6ADA' },
            }}
          >
            {React.cloneElement(item.icon, {
              sx: { ...(item.icon.props?.sx || {}), color: active ? '#0E6ADA' : '#666', fontSize: 24 }
            })}
            <Typography
              variant="caption"
              sx={{
                fontSize: "10px",
                mt: 0.2,
                display: "block",
                fontWeight: active ? 700 : 500,
                color: active ? '#0E6ADA' : '#888',
                lineHeight: 1.2,
              }}
            >
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default BottomNavMobile;
